import { DOCUMENT } from '@angular/common';
import {
  EnvironmentProviders,
  inject,
  Injectable,
  provideAppInitializer,
} from '@angular/core';
import { ITenantConfigResponse } from '@inshuwa-client/models';
import { baseUrl } from '../configs/baseUrl';
import { environment } from '../environments/environment';
import { setSentryTenant } from '../sentry';
import { resolveTenantDomain } from '../utils/tenant-domain.utils';
import { computeEnvironmentPatch } from './tenant-config.utils';
import { ThemeApplierService } from './theme-applier.service';

const CACHE_KEY_PREFIX = 'tenantBrandingConfig';
const FETCH_TIMEOUT_MS = 4000;

@Injectable({ providedIn: 'root' })
export class TenantConfigService {
  private readonly document = inject(DOCUMENT);
  private readonly themeApplier = inject(ThemeApplierService);

  /**
   * APP_INITIALIZER entry point. Never throws/rejects - the app must always finish bootstrapping,
   * even if the branding endpoint is slow, down, or the domain is unrecognized.
   */
  async load(): Promise<void> {
    const location = this.document.location;
    const domain = resolveTenantDomain(location?.search ?? '', location?.hostname ?? '');

    const cached = this.readCache(domain);
    if (cached) {
      this.applyAndSync(cached);
    }

    const fetched = await this.fetchConfig(domain);
    if (fetched) {
      this.applyAndSync(fetched);
      this.writeCache(domain, fetched);
    }
  }

  // Deliberately raw fetch(), not HttpClient: this is an anonymous, best-effort, pre-auth
  // bootstrap call, and IcHttpConfigInterceptor (used by every HttpClient request in this app)
  // unconditionally pops an error notification on any failed request - exactly the alarming,
  // visible failure this call is supposed to avoid. A plain fetch() never goes through it.
  private async fetchConfig(domain: string): Promise<ITenantConfigResponse | null> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
      const response = await fetch(
        `${baseUrl()}/tenant-config?domain=${encodeURIComponent(domain)}`,
        { signal: controller.signal },
      );
      return response.ok ? ((await response.json()) as ITenantConfigResponse) : null;
    } catch {
      return null;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private applyAndSync(config: ITenantConfigResponse): void {
    // Mutate in place (never reassign) - every existing read site (app.component.ts's `env`
    // binding, RouteTitleStrategy, canMatch guards) holds a reference to this same object.
    Object.assign(environment, computeEnvironmentPatch(config, environment));
    this.themeApplier.apply(config);
    // Restores the per-tenant attribution Sentry used to get from the build's environment.name.
    setSentryTenant(config.portalName);
  }

  private readCache(domain: string): ITenantConfigResponse | null {
    try {
      const raw = localStorage.getItem(this.cacheKey(domain));
      return raw ? (JSON.parse(raw) as ITenantConfigResponse) : null;
    } catch {
      return null;
    }
  }

  private writeCache(domain: string, config: ITenantConfigResponse): void {
    try {
      localStorage.setItem(this.cacheKey(domain), JSON.stringify(config));
    } catch {
      // Private browsing / quota exceeded - the cache is a nice-to-have, never fatal.
    }
  }

  private cacheKey(domain: string): string {
    return `${CACHE_KEY_PREFIX}:${domain}`;
  }
}

export function provideTenantTheme(): EnvironmentProviders {
  return provideAppInitializer(() => inject(TenantConfigService).load());
}
